import { useMemo, useRef, useState } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';
import * as XLSX from 'xlsx';

interface Props {
  proyectos: ProyectoMock[];
}

const STATUS_COLORS: Record<string, string> = {
  'En ejecución': 'bg-teal-500',
  'Aprobado': 'bg-emerald-500',
  'Finalizado': 'bg-slate-400',
  'En evaluación': 'bg-amber-400',
  'Con correcciones': 'bg-orange-400',
  'No pertinente': 'bg-red-400',
  'Pendiente': 'bg-slate-300',
};

const AREA_COLORS = ['bg-teal-500', 'bg-emerald-500', 'bg-amber-400', 'bg-orange-400', 'bg-violet-400', 'bg-rose-400'];
const AREA_HEX = ['#14b8a6', '#10b981', '#fbbf24', '#fb923c', '#a78bfa', '#fb7185'];

const monthlyTrend = [
  { mes: 'Ago', value: 3 },
  { mes: 'Sep', value: 5 },
  { mes: 'Oct', value: 4 },
  { mes: 'Nov', value: 7 },
  { mes: 'Dic', value: 6 },
  { mes: 'Ene', value: 4 },
  { mes: 'Feb', value: 8 },
  { mes: 'Mar', value: 10 },
];

// Preview modal
interface PreviewModalProps {
  imageUrl: string;
  title: string;
  onClose: () => void;
  onDownload: () => void;
}
function PreviewModal({ imageUrl, title, onClose, onDownload }: PreviewModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-800 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-teal-600">
              <i className="ri-image-line" />
            </span>
            Vista previa — {title}
          </h3>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 cursor-pointer rounded-md hover:bg-slate-100 transition-all">
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        <div className="p-6">
          <div className="bg-slate-50 rounded-lg p-3 border border-slate-200 mb-5">
            <img src={imageUrl} alt={title} className="w-full rounded object-contain max-h-72" />
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={onClose} className="whitespace-nowrap px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 cursor-pointer transition-all">
              Cancelar
            </button>
            <button
              onClick={onDownload}
              className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-sm font-semibold bg-[#00426A] hover:bg-[#02ACB5] text-white rounded-md cursor-pointer transition-all"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-download-2-line" /></span>
              Descargar imagen PNG
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardCharts({ proyectos }: Props) {
  const statusRef = useRef<HTMLDivElement>(null);
  const areaRef = useRef<HTMLDivElement>(null);
  const trendRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<{ url: string; title: string; chartRef: React.RefObject<HTMLDivElement | null> } | null>(null);
  const [capturing, setCapturing] = useState<string | null>(null);

  const byStatus = useMemo(() => {
    const map: Record<string, number> = {};
    proyectos.forEach((p) => { map[p.estado] = (map[p.estado] || 0) + 1; });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [proyectos]);

  const byArea = useMemo(() => {
    const map: Record<string, number> = {};
    proyectos.forEach((p) => { map[p.area] = (map[p.area] || 0) + 1; });
    const maxVal = Math.max(...Object.values(map));
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value], idx) => ({
        name,
        value,
        pct: Math.round((value / maxVal) * 100),
        color: AREA_COLORS[idx % AREA_COLORS.length],
        hex: AREA_HEX[idx % AREA_HEX.length],
      }));
  }, [proyectos]);

  const maxMonth = Math.max(...monthlyTrend.map((m) => m.value));

  // Capture chart as canvas image
  const captureChart = async (ref: React.RefObject<HTMLDivElement | null>, title: string) => {
    if (!ref.current) return;
    setCapturing(title);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(ref.current, { backgroundColor: '#ffffff', scale: 2 });
      const url = canvas.toDataURL('image/png');
      setPreview({ url, title, chartRef: ref });
    } catch {
      // fallback: show alert-style message
    } finally {
      setCapturing(null);
    }
  };

  const downloadImage = () => {
    if (!preview) return;
    const a = document.createElement('a');
    a.href = preview.url;
    a.download = `${preview.title.replace(/\s+/g, '_')}.png`;
    a.click();
    setPreview(null);
  };

  // Export Excel with chart data in separate sheets
  const exportExcel = () => {
    try {
      const wb = XLSX.utils.book_new();

      // Sheet 1: Distribution by status
      const statusData = [
        ['Estado', 'Cantidad', 'Porcentaje (%)'],
        ...byStatus.map(([estado, count]) => [
          estado,
          count,
          proyectos.length > 0 ? Math.round((count / proyectos.length) * 100) : 0,
        ]),
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(statusData);
      ws1['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Distribucion por Estado');

      // Sheet 2: By area
      const areaData = [
        ['Area', 'Cantidad de proyectos'],
        ...byArea.map(({ name, value }) => [name, value]),
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(areaData);
      ws2['!cols'] = [{ wch: 35 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Proyectos por Area');

      // Sheet 3: Monthly trend
      const trendData = [
        ['Mes', 'Proyectos recibidos'],
        ...monthlyTrend.map(({ mes, value }) => [mes, value]),
      ];
      const ws3 = XLSX.utils.aoa_to_sheet(trendData);
      ws3['!cols'] = [{ wch: 12 }, { wch: 22 }];
      XLSX.utils.book_append_sheet(wb, ws3, 'Tendencia de Recepcion');

      // Sheet 4: All projects summary
      const allData = [
        ['Codigo', 'Titulo', 'Investigador', 'Area', 'Categoria', 'Estado', 'Decision', 'Etica', 'Avance (%)', 'Fecha Recepcion', 'Fecha Decision'],
        ...proyectos.map((p) => [
          p.codigo, p.titulo, p.investigador, p.area, p.categoria,
          p.estado, p.decision, p.etica, p.porcentaje,
          p.fechaRecepcion, p.fechaDecision,
        ]),
      ];
      const ws4 = XLSX.utils.aoa_to_sheet(allData);
      ws4['!cols'] = [{ wch: 16 }, { wch: 45 }, { wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 18 }, { wch: 28 }, { wch: 8 }, { wch: 12 }, { wch: 16 }, { wch: 16 }];
      XLSX.utils.book_append_sheet(wb, ws4, 'Todos los Proyectos');

      // Use Blob for reliable browser download
      const wbOut = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbOut], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'UGPP_Dashboard_Datos.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando Excel:', err);
    }
  };

  return (
    <>
      {preview && (
        <PreviewModal
          imageUrl={preview.url}
          title={preview.title}
          onClose={() => setPreview(null)}
          onDownload={downloadImage}
        />
      )}

      {/* Export global bar */}
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mb-4">
        <p className="text-xs font-semibold text-slate-600 flex items-center gap-2">
          <span className="w-4 h-4 flex items-center justify-center text-teal-600">
            <i className="ri-download-cloud-2-line" />
          </span>
          Exportar datos de gráficas
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={exportExcel}
            className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md cursor-pointer transition-all"
          >
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-excel-line" /></span>
            Descargar Excel (todas las hojas)
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-6">
        {/* Status distribution */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-pie-chart-2-line text-teal-600" />
              </span>
              Distribución por estado
            </h4>
            <button
              onClick={() => captureChart(statusRef, 'Distribución_por_Estado')}
              disabled={capturing === 'Distribución_por_Estado'}
              className="whitespace-nowrap flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 border border-teal-200 hover:border-teal-400 px-2 py-1 rounded-md cursor-pointer transition-all disabled:opacity-50"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className={capturing === 'Distribución_por_Estado' ? 'ri-loader-4-line animate-spin' : 'ri-image-line'} />
              </span>
              {capturing === 'Distribución_por_Estado' ? 'Capturando...' : 'Vista previa / PNG'}
            </button>
          </div>
          <div ref={statusRef} className="space-y-3 bg-white p-1">
            {byStatus.map(([status, count]) => {
              const pct = Math.round((count / proyectos.length) * 100);
              const bg = STATUS_COLORS[status] || 'bg-slate-300';
              return (
                <div key={status}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-slate-600 font-medium">{status}</span>
                    <span className="text-slate-400 font-semibold">{count} · {pct}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2">
                    <div className={`${bg} h-2 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By area */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-bar-chart-horizontal-line text-teal-600" />
              </span>
              Proyectos por área
            </h4>
            <button
              onClick={() => captureChart(areaRef, 'Proyectos_por_Área')}
              disabled={capturing === 'Proyectos_por_Área'}
              className="whitespace-nowrap flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 border border-teal-200 hover:border-teal-400 px-2 py-1 rounded-md cursor-pointer transition-all disabled:opacity-50"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className={capturing === 'Proyectos_por_Área' ? 'ri-loader-4-line animate-spin' : 'ri-image-line'} />
              </span>
              {capturing === 'Proyectos_por_Área' ? 'Capturando...' : 'Vista previa / PNG'}
            </button>
          </div>
          <div ref={areaRef} className="space-y-3 bg-white p-1">
            {byArea.map(({ name, value, pct, color }) => (
              <div key={name}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-600 font-medium truncate max-w-[75%]">{name}</span>
                  <span className="text-slate-400 font-semibold">{value}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className={`${color} h-2 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly trend */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-line-chart-line text-teal-600" />
              </span>
              Tendencia de recepción
            </h4>
            <button
              onClick={() => captureChart(trendRef, 'Tendencia_Recepción')}
              disabled={capturing === 'Tendencia_Recepción'}
              className="whitespace-nowrap flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 border border-teal-200 hover:border-teal-400 px-2 py-1 rounded-md cursor-pointer transition-all disabled:opacity-50"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className={capturing === 'Tendencia_Recepción' ? 'ri-loader-4-line animate-spin' : 'ri-image-line'} />
              </span>
              {capturing === 'Tendencia_Recepción' ? 'Capturando...' : 'Vista previa / PNG'}
            </button>
          </div>
          <div ref={trendRef} className="bg-white p-1">
            <div className="flex items-end gap-1.5" style={{ height: '100px' }}>
              {monthlyTrend.map(({ mes, value }) => {
                const barH = Math.round((value / maxMonth) * 80);
                const isMax = value === maxMonth;
                return (
                  <div key={mes} className="flex-1 flex flex-col items-center gap-1 justify-end h-full">
                    <span className="text-xs text-slate-400 font-medium">{value}</span>
                    <div
                      className={`w-full rounded-t transition-all duration-500 ${isMax ? 'bg-teal-500' : 'bg-teal-200'}`}
                      style={{ height: `${barH}px` }}
                    />
                    <span className="text-xs text-slate-400">{mes}</span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">Proyectos recibidos por mes (2025–2026)</p>
          </div>
        </div>
      </div>
    </>
  );
}